<template>
  <b-modal
    v-model="isShown"
    id="be-location-nearby-modal"
    centered
    size="lg"
    hide-header
    hide-footer
    body-class="p-0">
    <div class="be-location-modal" :style="fontVars">
      <div class="be-location-modal__head">
        <div>
          <p class="be-location-modal__title">Location</p>
          <p class="be-location-modal__subtitle">Location of the property</p>
        </div>
        <button type="button" class="be-location-modal__close" @click="$emit('close')">
          <X :size="18" />
        </button>
      </div>

      <div class="be-location-modal__body">
        <iframe
          v-if="hasMapEmbed"
          :src="locationEmbedMap"
          width="100%"
          height="260"
          style="border: 0"
          class="be-location-modal__map"
          allowfullscreen=""
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"></iframe>

        <div v-if="hasAddress" class="be-location-modal__address">
          <MapPin :size="16" class="be-location-modal__address-icon" />
          <span>{{ merchant.address }}</span>
        </div>
        <p class="be-location-modal__description" v-if="hasLocationDescription">
          {{ merchant.location_description }}
        </p>
        <div v-if="hasLocationUrl" class="be-location-modal__actions">
          <a
            :href="merchant.location_url"
            target="_blank"
            rel="noopener noreferrer"
            class="be-location-modal__action-btn">
            <ExternalLink :size="15" />
            Open in Maps
          </a>
          <a
            :href="merchant.location_url"
            target="_blank"
            rel="noopener noreferrer"
            class="be-location-modal__action-btn">
            <Navigation :size="15" />
            Direction
          </a>
        </div>

        <hr class="be-location-modal__divider" />

        <h3 class="be-location-modal__nearby-title">Nearby Destinations</h3>

        <div class="be-location-modal__tabs hide-scroll">
          <button
            type="button"
            class="be-location-modal__tab"
            :class="{ 'be-location-modal__tab--active': selectedCategoryId === tab.id }"
            v-for="tab in categoryTabs"
            :key="tab.id"
            @click="selectedCategoryId = tab.id">
            {{ tab.name }} ({{ tab.count }})
          </button>
        </div>

        <div class="be-location-modal__list">
          <div v-for="place in filteredList" :key="place.id" class="be-location-modal__item">
            <img
              v-if="place.place_category && place.place_category.image_url"
              :src="place.place_category.image_url"
              :alt="place.place_category.name"
              class="be-location-modal__item-icon" />
            <div class="be-location-modal__item-info">
              <p class="be-location-modal__item-name">{{ place.name }}</p>
              <p class="be-location-modal__item-category">{{ place.place_category && place.place_category.name }}</p>
              <p class="be-location-modal__item-distance">
                <span class="be-location-modal__item-distance-value">{{ place.distance }}</span>
                from the property
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { X, MapPin, ExternalLink, Navigation } from 'lucide-vue';
import { getMerchantMapEmbedUrl } from '@/utils/merchantLocation.js';

const NEAREST_TAB_ID = 'nearest';

export default {
  name: 'BeLocationNearbyModal',
  components: {
    X,
    MapPin,
    ExternalLink,
    Navigation,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    merchant: {
      type: Object,
      default: () => ({}),
    },
    nearbyList: {
      type: Array,
      default: () => [],
    },
    // resolved --be-v3-*-font values from the parent (see BeLocationNearby.vue) --
    // this modal is teleported to <body> by bootstrap-vue, so it can't inherit
    // them via CSS cascade.
    fontVars: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedCategoryId: NEAREST_TAB_ID,
    };
  },
  computed: {
    isShown: {
      get() {
        return this.isOpen;
      },
      set(isShown) {
        if (!isShown) this.$emit('close');
      },
    },
    locationEmbedMap() {
      return getMerchantMapEmbedUrl(this.merchant);
    },
    hasMapEmbed() {
      return this.$isNotEmptyV2(this.locationEmbedMap);
    },
    hasAddress() {
      return this.$isNotEmptyV2(this.merchant.address);
    },
    hasLocationDescription() {
      return this.$isNotEmptyV2(this.merchant.location_description);
    },
    hasLocationUrl() {
      return this.$isNotEmptyV2(this.merchant.location_url);
    },
    categoryTabs() {
      const categories = [];
      this.nearbyList.forEach((place) => {
        const category = place.place_category;
        if (!category) return;
        const existing = categories.find((item) => item.id === category.id);
        if (existing) {
          existing.count += 1;
        } else {
          categories.push({ id: category.id, name: category.name, count: 1 });
        }
      });
      return [{ id: NEAREST_TAB_ID, name: 'Nearest', count: this.nearbyList.length }, ...categories];
    },
    filteredList() {
      if (this.selectedCategoryId === NEAREST_TAB_ID) return this.nearbyList;
      return this.nearbyList.filter(
        (place) => place.place_category && place.place_category.id === this.selectedCategoryId
      );
    },
  },
  watch: {
    isOpen(isOpen) {
      if (isOpen) this.selectedCategoryId = NEAREST_TAB_ID;
    },
  },
};
</script>

<style scoped>
/* This modal is teleported to <body> (bootstrap-vue's BVTransporter), so it never
   sits under SearchRoomLandingPage's .be-landing to inherit --be-v3-*-font from --
   the `fontVars` prop (bound via :style above) restates the parent's resolved
   value inline there. Elsewhere it's unset, so `inherit` falls through to
   <body>'s own ambient font, matching each of those pages' native styling. */
.be-location-modal {
  font-family: var(--be-v3-content-font, inherit);
}
.be-location-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 24px;
  border-bottom: 1px solid #e8e8e8;
}
.be-location-modal__title {
  margin: 0 0 4px;
  font-family: var(--be-v3-title-font, inherit);
  font-size: 1.375rem;
  font-weight: var(--be-location-title-weight, 500);
  color: #1a1a2e;
}
.be-location-modal__subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: #777;
}
.be-location-modal__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #fff;
  border: 1px solid #d5d9e0;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  cursor: pointer;
}
.be-location-modal__body {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}
.be-location-modal__map {
  width: 100%;
  display: block;
  border-radius: var(--be-v3-radius, 14px);
  overflow: hidden;
}
.be-location-modal__address {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  color: #1a1a2e;
  font-size: 15px;
  font-weight: var(--be-location-bold-weight, 600);
}
.be-location-modal__address-icon {
  flex-shrink: 0;
  color: #999;
}
.be-location-modal__description {
  margin: 10px 0 0;
  color: #555;
  font-size: 14px;
  line-height: 1.6;
}
.be-location-modal__actions {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}
.be-location-modal__divider {
  margin: 24px 0;
  border: none;
  border-top: 1px solid #e8e8e8;
}
.be-location-modal__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #d5d9e0;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  background: #fff;
  text-decoration: none;
}
.be-location-modal__action-btn:hover {
  color: #1a1a2e;
  text-decoration: none;
  background: #f5f6f8;
}
.be-location-modal__nearby-title {
  margin: 0 0 14px;
  font-family: var(--be-v3-title-font, inherit);
  font-size: 1.125rem;
  font-weight: var(--be-location-title-weight, 500);
  color: #1a1a2e;
}
.be-location-modal__tabs {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  margin-bottom: 18px;
}
.be-location-modal__tab {
  flex-shrink: 0;
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
  cursor: pointer;
}
.be-location-modal__tab--active {
  color: var(--merchant-primary-color, #79a33a);
  border-color: var(--merchant-primary-color, #79a33a);
  background: color-mix(in srgb, var(--merchant-primary-color, #79a33a) 10%, #fff);
}
.be-location-modal__list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px 24px;
}
@media (max-width: 575.98px) {
  .be-location-modal__list {
    grid-template-columns: 1fr;
  }
}
.be-location-modal__item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.be-location-modal__item-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}
.be-location-modal__item-name {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: var(--be-location-bold-weight, 600);
  color: #1a1a2e;
}
.be-location-modal__item-category {
  margin: 0 0 4px;
  font-size: 13px;
  color: #999;
}
.be-location-modal__item-distance {
  margin: 0;
  font-size: 13px;
  color: #777;
}
.be-location-modal__item-distance-value {
  font-weight: var(--be-location-bold-weight, 600);
  color: #1a1a2e;
}
</style>

<style>
/* BModal wraps itself in an outer position:absolute div (id suffixed
   __BV_modal_outer_) carrying an inline z-index from bootstrap-vue's modalManager --
   that outer z-index, not .modal/.modal-backdrop's own, is what decides stacking
   against page content outside the modal. Booking pages (RoomDesktop/RoomMobile/
   SearchRoomWithMultiImageBanner) run their own sticky search bar up to z-index 1046
   (see style.css .z-index-104x) !important, above the outer wrapper's default value,
   so the bar showed through over the backdrop. Unscoped and ID-selector-only so it
   targets exactly this modal instance -- !important is required to beat the inline
   style. */
#be-location-nearby-modal___BV_modal_outer_ {
  z-index: 1060 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeLocationNearbyModal.vue